import type { ShopI18nContextValue } from '../../../i18n/provider/types'
import { type useCustomerAuthForm } from '../../hooks/useCustomerAuthForm'
import { CustomerAuthForm } from './CustomerAuthForm'
import { useCustomerAuthFormBlockModel } from './useCustomerAuthFormBlockModel'

type CustomerAuthFormBlockProps = {
  busy: boolean
  initializing: boolean
  botProtectionEnabled: boolean
  botProtectionProviderId: string | null
  botProtectionClientConfig: Record<string, unknown> | null
  locale: string
  t: ShopI18nContextValue['t']
  formState: ReturnType<typeof useCustomerAuthForm>
  onForgotPassword: () => void
  onGoToLogin: () => void
  onGoToHome: () => void
}

export const CustomerAuthFormBlock = ({
  busy,
  initializing,
  botProtectionEnabled,
  botProtectionProviderId,
  botProtectionClientConfig,
  locale,
  t,
  formState,
  onForgotPassword,
  onGoToLogin,
  onGoToHome,
}: CustomerAuthFormBlockProps) => {
  const model = useCustomerAuthFormBlockModel({
    locale,
    t,
    fieldErrorKeys: formState.fieldErrorKeys,
  })

  const valueProps = {
    email: formState.email,
    password: formState.password,
    confirmPassword: formState.confirmPassword,
    firstName: formState.firstName,
    lastName: formState.lastName,
    taxCode: formState.taxCode,
    gender: formState.gender,
    phonePrefix: formState.phonePrefix,
    phoneLocalNumber: formState.phoneLocalNumber,
    addressStreet: formState.addressStreet,
    addressStreetNumber: formState.addressStreetNumber,
    addressCity: formState.addressCity,
    addressPostalCode: formState.addressPostalCode,
    addressProvince: formState.addressProvince,
    addressCountry: formState.addressCountry,
  }

  const handlerProps = {
    onEmailChange: formState.setEmail,
    onPasswordChange: formState.setPassword,
    onConfirmPasswordChange: formState.setConfirmPassword,
    onFirstNameChange: formState.setFirstName,
    onLastNameChange: formState.setLastName,
    onTaxCodeChange: formState.setTaxCode,
    onGenderChange: formState.setGender,
    onPhonePrefixChange: formState.setPhonePrefix,
    onPhoneLocalNumberChange: formState.setPhoneLocalNumber,
    onAddressStreetChange: formState.setAddressStreet,
    onAddressStreetNumberChange: formState.setAddressStreetNumber,
    onAddressCityChange: formState.setAddressCity,
    onAddressPostalCodeChange: formState.setAddressPostalCode,
    onAddressProvinceChange: formState.setAddressProvince,
    onAddressCountryChange: formState.setAddressCountry,
  }

  return (
    <CustomerAuthForm
      isSignup={formState.isSignup}
      busy={busy}
      initializing={initializing}
      signupStep={formState.signupStep}
      totalSignupSteps={formState.totalSignupSteps}
      genderOptions={model.genderOptions}
      phonePrefixOptions={model.phonePrefixOptions}
      addressCountryOptions={model.countryOptions}
      botProtectionEnabled={botProtectionEnabled}
      botProtectionProviderId={botProtectionProviderId}
      botProtectionClientConfig={botProtectionClientConfig}
      botProtectionToken={formState.botProtectionToken}
      fieldErrors={model.fieldErrors}
      onFieldBlur={formState.touchField}
      onNextSignupStep={formState.goToNextSignupStep}
      onPreviousSignupStep={formState.goToPreviousSignupStep}
      onJumpToSignupStep={formState.goToSignupStep}
      onForgotPassword={onForgotPassword}
      onBotProtectionTokenChange={formState.setBotProtectionToken}
      onGoToLogin={onGoToLogin}
      onGoToHome={onGoToHome}
      onSubmit={formState.submit}
      {...model.labels}
      {...valueProps}
      {...handlerProps}
    />
  )
}
